import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { formatBRL, formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";

const EMPTY = {
  name: "", description: "", price: "", category: "", images: "", sizes: "", colors: "", stock: "", featured: false,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", price: String(p.price),
      category: p.category, images: (p.images || []).join("\n"),
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "),
      stock: String(p.stock ?? 0), featured: !!p.featured,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, description: form.description, price: parseFloat(form.price) || 0,
      category: form.category,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock) || 0, featured: form.featured,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produto atualizado" : "Produto criado");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Produto excluído");
    load();
  };

  const inputCls = "w-full bg-[#151515] border border-white/10 focus:border-[#B89C65] outline-none px-3 py-2 text-sm transition-colors";

  return (
    <AdminLayout title="Produtos">
      <div className="flex justify-end mb-6">
        <button data-testid="new-product-btn" onClick={openNew} className="flex items-center gap-2 bg-[#B89C65] text-white px-5 py-3 overline hover:bg-[#A6884F] transition-colors">
          <Plus size={16} /> Novo produto
        </button>
      </div>

      <div className="bg-[#0f0f0f] border border-white/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/40 overline border-b border-white/5">
              <th className="px-5 py-4">Produto</th>
              <th className="px-5 py-4">Categoria</th>
              <th className="px-5 py-4">Preço</th>
              <th className="px-5 py-4">Estoque</th>
              <th className="px-5 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-white/5 last:border-0">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 bg-white/5 overflow-hidden">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span>{p.name}{p.featured && <span className="ml-2 text-[10px] text-[#B89C65] overline">destaque</span>}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-white/60">{p.category}</td>
                <td className="px-5 py-3">{formatBRL(p.price)}</td>
                <td className="px-5 py-3 text-white/60">{p.stock}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-2 justify-end">
                    <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-white/50 hover:text-[#B89C65] transition-colors"><Pencil size={16} /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => remove(p)} className="p-2 text-white/50 hover:text-[#B89C65] transition-colors"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="product-form"
            className="bg-[#0f0f0f] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">{editing ? "Editar produto" : "Novo produto"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="overline text-white/50 block mb-1">Nome</label>
                <input data-testid="pf-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="overline text-white/50 block mb-1">Descrição</label>
                <textarea data-testid="pf-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-white/50 block mb-1">Preço (R$)</label>
                  <input data-testid="pf-price" required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="overline text-white/50 block mb-1">Estoque</label>
                  <input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="overline text-white/50 block mb-1">Categoria</label>
                <input data-testid="pf-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} placeholder="Vestidos, Blazers…" />
              </div>
              <div>
                <label className="overline text-white/50 block mb-1">Imagens (uma URL por linha)</label>
                <textarea data-testid="pf-images" rows={2} value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-white/50 block mb-1">Tamanhos (vírgula)</label>
                  <input data-testid="pf-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className={inputCls} placeholder="P, M, G" />
                </div>
                <div>
                  <label className="overline text-white/50 block mb-1">Cores (vírgula)</label>
                  <input data-testid="pf-colors" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className={inputCls} placeholder="Preto, Bege" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                Marcar como destaque
              </label>
            </div>
            <button data-testid="pf-save" disabled={saving} className="w-full bg-[#B89C65] text-white py-3 mt-6 overline hover:bg-[#A6884F] transition-colors disabled:opacity-60">
              {saving ? "Salvando…" : "Salvar"}
            </button>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
